import React, {FC} from 'react';
import {Redirect, useLocation} from 'react-router-dom';

import {SingleRunNotFound} from '../../components/MultiRunWorkspace/SingleRunMode/SingleRunNotFound';
import {LoaderProxy} from '../../containers/AppLoader';
import {useRunByDisplayNameQuery} from '../../generated/graphql';
import * as urls from '../../util/urls';
import {useProjectParams} from './useProjectParams';

/**
 * Component that handles runDisplayName query parameter for runs paths.
 * Works for project runs: /:entityName/:projectName/runs?runDisplayName=...
 *
 * Redirects to the most recent run with the given display name.
 * If no run is found with that display name, shows the SingleRunNotFound component.
 * If no runDisplayName query parameter is provided, redirects to the project page.
 */
export const RunsRedirect: FC = () => {
  const {entityName, projectName} = useProjectParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const runDisplayName = searchParams.get('runDisplayName');

  const {loading, error, data} = useRunByDisplayNameQuery({
    variables: {
      entityName,
      projectName,
      filters: JSON.stringify({
        displayName: runDisplayName,
      }),
    },
    skip: !runDisplayName,
    fetchPolicy: 'network-only',
  });

  // If no runDisplayName query param, redirect to parent page
  if (!runDisplayName) {
    return <Redirect to={urls.project({entityName, name: projectName})} />;
  }

  if (loading) {
    return <LoaderProxy name="run-by-display-name-loading" />;
  }

  if (error || !data?.project || !data.project.runs) {
    console.error('Error loading run by display name:', error);
    return <SingleRunNotFound />;
  }

  const runs = data.project.runs.edges;

  if (runs.length === 0) {
    return <SingleRunNotFound />;
  }

  // Redirect to the first run found, preserving other query parameters
  const run = runs[0].node;
  searchParams.delete('runDisplayName');
  const newSearch = searchParams.toString();

  // Build redirect URL
  const redirectUrl = urls.run({
    entityName,
    projectName,
    name: run.name,
  });

  return (
    <Redirect
      to={{
        pathname: redirectUrl,
        search: newSearch ? `?${newSearch}` : '',
      }}
    />
  );
};

export default RunsRedirect;
